// Who asked and what was called, in the person's words, for the rows of a
// solution's declared list (02-DESIGN/11-observe/03-the-observability-
// solution.md § the console, § attribution; 02-DESIGN/03-console/01-the-
// console.md § the words, § conventions: density and length). A row names
// a person by their display name, an app by its name, a workload by its
// name, and a solution's own workload as the solution; the identifier is
// secondary, shortened beside the name. The address a record names reads in
// the verb table's words where a row of it names the subject, and is
// otherwise the subject itself, shortened from the middle with copy. Nothing
// here knows React or the bus: the names are what the page read on the
// apps list and the workloads list, and the install records.

import { tenantPath } from "../../address/address.ts";
import { addressWords as wordsOf, tokensOf } from "../../wire/addresses.ts";
import { ElementKind, type Panel } from "../../wire/solutions.ts";
import type { Row } from "./panel.ts";

/** A workload as the page knows it: its name and, for a solution's, the solution's display name. */
export interface Known {
  name: string;
  solution?: string;
}

/** Names are the identifiers the page can name: apps, workloads and installed solutions. */
export interface Names {
  /** App id → the app's name. */
  apps: ReadonlyMap<string, string>;
  /** Workload id → its name and its solution. */
  workloads: ReadonlyMap<string, Known>;
  /** Solution name → its display name. */
  solutions: ReadonlyMap<string, string>;
}

export const NO_NAMES: Names = { apps: new Map(), workloads: new Map(), solutions: new Map() };

/** WhoKind is what a row's caller is. */
export type WhoKind = "person" | "app" | "workload" | "solution" | "unverified" | "nobody";

/** Who is a caller in words: the name, the identifier beside it, and how it is known. */
export interface Who {
  name: string;
  /** The identifier, shown shortened beside the name; absent where there is none. */
  id?: string;
  kind: WhoKind;
  /** The caller the platform attested where no carrier named one (§ attribution). */
  attested?: boolean;
}

function obj(v: unknown): Record<string, unknown> | undefined {
  return typeof v === "object" && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : undefined;
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

/** solutionWords is a solution's own workload in words: *the Observability solution*. */
export function solutionWords(display: string): string {
  return `the ${display} solution`;
}

/** workloadWho names a workload by its id: a solution's as the solution, else by its name. */
export function workloadWho(id: string, n: Names): Who {
  const w = n.workloads.get(id);
  if (w?.solution !== undefined) {
    return { name: solutionWords(w.solution), id, kind: "solution" };
  }
  return { name: w?.name ?? "a workload", id, kind: "workload" };
}

function appWho(id: string, n: Names, display?: string): Who {
  const name = n.apps.get(id);
  if (name === undefined) {
    return { name: display !== undefined && display !== "" ? display : "an app", id, kind: "app" };
  }
  return {
    name: display !== undefined && display !== "" && display !== name ? `${name}, as ${display}` : name,
    id,
    kind: "app",
  };
}

/**
 * attestations reads the request info's user the platform composed at the
 * callout — `role:member principal:… app:… workload:…` (decision 0029) — into
 * its tags; empty where it holds none.
 */
export function attestations(user: string): Record<string, string> {
  const out: Record<string, string> = {};
  if (!user.includes(":")) {
    return out;
  }
  for (const pair of user.split(" ")) {
    const at = pair.indexOf(":");
    if (at > 0 && at < pair.length - 1) {
      out[pair.slice(0, at)] = pair.slice(at + 1);
    }
  }
  return out;
}

/**
 * ownerOf is the solution whose own things an address names — the vault's
 * sign of a checkpoint under the observability solution's keys — or undefined. Only a
 * solution's workloads reach its keys without a person's carrier, so a call
 * there that names nobody is the solution's own.
 */
export function ownerOf(address: string, n: Names): string | undefined {
  const toks = tokensOf(address);
  const at = toks.indexOf("solution");
  if (at < 1 || at + 1 >= toks.length) {
    return undefined;
  }
  const name = toks[at + 1] ?? "";
  return n.solutions.get(name) ?? (name === "" ? undefined : name);
}

/**
 * whoOf is who asked a record, as the list shows it: a carrier that did not
 * verify as such, never replaced; the carrier's principal — a person, an app,
 * a workload — where one names it; otherwise the caller the platform
 * attested, marked as attested; otherwise, for a call on a solution's own
 * things, the solution; otherwise nobody.
 */
export function whoOf(row: Row, n: Names): Who {
  const p = obj(row.principal);
  if (p !== undefined && str(p.carrier) === "invalid") {
    return { name: "could not be verified", kind: "unverified" };
  }
  const token = str(p?.token);
  const display = str(p?.display);
  if (p !== undefined && (token !== "" || display !== "")) {
    const app = str(p.app);
    if (str(p.kind) === "app" || app !== "") {
      return appWho(app !== "" ? app : token, n, display);
    }
    if (str(p.kind) === "workload") {
      return workloadWho(token, n);
    }
    return {
      name: display !== "" ? display : "a person",
      id: token === "" ? undefined : token,
      kind: "person",
    };
  }
  const att = attestations(str(obj(row.reporter)?.caller));
  if (att.app !== undefined) {
    return { ...appWho(att.app, n), attested: true };
  }
  if (att.workload !== undefined) {
    return { ...workloadWho(att.workload, n), attested: true };
  }
  if (att.principal !== undefined) {
    return { name: "a person", id: att.principal, kind: "person", attested: true };
  }
  const owner = ownerOf(str(row.address), n);
  if (owner !== undefined) {
    return { name: solutionWords(owner), kind: "solution" };
  }
  const who = str(row.who);
  return who === "" ? { name: "nobody named", kind: "nobody" } : { name: who, kind: "person" };
}

/**
 * rowName is a ranked row's name — a usage list's *by person*, *by app*, *by
 * model* — beside its key: the name the row carries where it is more than
 * the key, else the app's or the workload's name the page knows the key by.
 * The key is shown beside the name wherever it differs, so two rows of one
 * name — one person signed in through two sign-ins, each a principal of its
 * own — read apart.
 */
export function rowName(name: string, key: string | undefined, n: Names): Who {
  if (key === undefined || key === "") {
    return { name, kind: "person" };
  }
  const own = name !== "" && name !== key;
  if (n.apps.has(key)) {
    return { ...appWho(key, n), ...(own ? { name } : {}) };
  }
  if (n.workloads.has(key)) {
    const w = workloadWho(key, n);
    return own ? { ...w, name } : w;
  }
  return { name: own ? name : "", id: key, kind: "person" };
}

/**
 * addressWords is a record's address in the design's words — *Stop
 * support-bot*, *Chat with gpt-review* — or undefined where no row of the
 * verb table names it, which the list then shows as the subject itself.
 */
export function addressWords(address: string, n: Names): string | undefined {
  return wordsOf(address, {
    workload: (id) => {
      const w = n.workloads.get(id);
      return w?.solution !== undefined ? solutionWords(w.solution) : (w?.name ?? "a workload");
    },
    solution: (name) => solutionWords(n.solutions.get(name) ?? name),
  });
}

/** Linked is a page a name on a declared element may link to: the things an audit record and a usage list name. */
export type Linked = "apps" | "workloads" | "guardrails" | "models";

/**
 * whoPath is the page of who asked, where it has one the role is offered
 * (01-the-console.md § conventions: a name is a link): an app's,
 * `apps/<id>`, or a workload's — a solution's own included — `workloads/<id>`.
 * A person has no address the record can build: their page is keyed by the
 * provider, the claim and the value of their member row, which a record does
 * not carry, so a person's name stays text.
 */
export function whoPath(w: Who, tenant: string, may: (page: Linked) => boolean): string | undefined {
  if (w.id === undefined || w.id === "") {
    return undefined;
  }
  if (w.kind === "app" && may("apps")) {
    return tenantPath(tenant, "apps", w.id);
  }
  if ((w.kind === "workload" || w.kind === "solution") && may("workloads")) {
    return tenantPath(tenant, "workloads", w.id);
  }
  return undefined;
}

/**
 * fieldPath is the page a record's field names, where it has one the role is
 * offered: the app that asked (`app`), the workload that served it
 * (`served-by`), the principal where the record says it is an app or a
 * workload (`principal` beside `kind`), and the guardrail rule that judged it
 * (`rule`). The fields are the record verb's flat names
 * (11-observe/03 § the verbs, `record`); any other field stays a value.
 */
export function fieldPath(
  field: string,
  row: Row,
  tenant: string,
  may: (page: Linked) => boolean,
): string | undefined {
  const v = row[field];
  if (typeof v !== "string" || v === "") {
    return undefined;
  }
  const to = (page: Linked) => (may(page) ? tenantPath(tenant, page, v) : undefined);
  switch (field) {
    case "app":
      return to("apps");
    case "served-by":
      return to("workloads");
    case "rule":
      return to("guardrails");
    case "principal":
      return row.kind === "workload" ? to("workloads") : row.kind === "app" ? to("apps") : undefined;
  }
  return undefined;
}

/** MODEL_DIMENSIONS are the dimensions a ranked list ranks models by: its rows are keyed by the model's workload id. */
const MODEL_DIMENSIONS: ReadonlySet<string> = new Set(["model", "workload"]);

/**
 * rankedPath is the page a ranked list's row names — a usage list's *by app*,
 * *by model*, *by person* — where it has one the role is offered: a model's,
 * `models/<id>`, where the list ranks by model, whose rows are keyed by the
 * model's workload id (11-observe/03 § the verbs, `usage-list`); otherwise as
 * who asked, an app's or a workload's page, and a person's name stays text.
 */
export function rankedPath(
  w: Who,
  dimension: string | undefined,
  tenant: string,
  may: (page: Linked) => boolean,
): string | undefined {
  if (w.id === undefined || w.id === "") {
    return undefined;
  }
  if (dimension !== undefined && MODEL_DIMENSIONS.has(dimension)) {
    return may("models") ? tenantPath(tenant, "models", w.id) : undefined;
  }
  return whoPath(w, tenant, may);
}

/**
 * chipPath is the page a selection chip names, where it has one the role is
 * offered. A selecting row's first cell is its opener — choosing it narrows
 * the panel — so it holds no link, and the way to the thing chosen is the
 * chip: the list whose rows set the field says what its keys are by its
 * dimension, an app's (`apps/<id>`) or a model's (`models/<id>`, keyed by
 * its workload id). A person, a day, and a field no ranked list sets stay
 * text.
 */
export function chipPath(
  p: Panel,
  field: string,
  value: string,
  tenant: string,
  may: (page: Linked) => boolean,
): string | undefined {
  const list = p.elements.find((e) => e.kind === ElementKind.list && e.selects === field);
  const dimension = list?.params?.dimension;
  if (dimension === undefined || value === "") {
    return undefined;
  }
  const kind = dimension === "app" ? "app" : "person";
  return rankedPath({ name: "", id: value, kind }, dimension, tenant, may);
}
